import type {
  InputDescription,
  IntegrationDescription,
} from "@smartsuite/automation-engine-api";
import type { FieldTypePillProps } from "@smartsuite/react-ui/lib";
import {
  DatePicker,
  DropdownPanel,
  MenuGroupContent,
  MenuOption,
  MenuOptionWrapper,
  PickerInputField,
} from "@smartsuite/react-ui/lib";
import type { PillPickerEditorCommands } from "@smartsuite/smartdoc";
import type { PillNodeAttrs } from "@smartsuite/smartdoc/lib/esm/extensions/pill/extension";
import i18n, { t } from "i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import * as R from "remeda";
import { PickerStyleWrapper } from "../../../../../../../../components/PickerStyleWrapper";
import type {
  ActionCandidate,
  InputCandidate,
  TriggerCandidate,
} from "../../../../../../../../types/candidate";
import type {
  DynamicSection,
  DynamicSectionOption,
  DynamicSectionOptionGroup,
} from "../../../../../../../../types/line";
import { useConverters } from "../../../../../../../../utils/conditions-coverter.hook";
import { getLocaleByLangCode } from "../../../../../../../../utils/locale";
import {
  convertActionsToDynamicSections,
  convertTriggerToDynamicSection,
} from "../../../../utils";
import { TriggerOptions } from "../../containers/TriggerOptions";
import { calculateConfigValue, checkIfIsPickerInputField } from "../utils";
import { DatePickerWrapper, InputWrapper } from "./style";
import { NumberInput } from "./number-input";
import {
  daysToOffset,
  parseDaysOffset,
} from "../../../../../../../../utils/days";
import { CaptionBlock } from "../../../../../../../../components/CaptionBlock";
import { getLocalCssColorVariables } from "../../../../../../../../utils/cssVariablesUtils";
import { useTheme } from "../../../../../../../../stores";
import { ValueDescription_T } from "@smartsuite/automation-engine-api";
import {
  ActionLabel,
  MENU_MAX_HEIGHT,
  MENU_MAX_HEIGHT_MARGIN,
} from "../../../../../../../../utils/constants";
import { POSITION_AT, POSITION_MY } from "../../../../style";
import { useRootStore } from "../../../../../../../../stores/hooks";
import { ActionOptions } from "../../containers/ActionOptions";
import { useHideInterval } from "./hide-interval.hook";
import { TimeEditor } from "./TimeEditor";
import { useTime } from "./time.hook";
import {
  convertDateToBrowserTimezone,
  createDateInUserTimezone,
} from "../../../../../../../../utils/date";
import { getTimezoneShortLabel } from "../../../../../../../../timezone/timezones";
import { FieldMissingError } from "../../../../../../Sections/ErrorSection";
import { useFieldMissingValidation } from "../../../../../../../../stores/automation/hooks/field-missing-validation";
import { SubMenuOptions } from "../../containers/SubMenuOptions";
import { Tab } from "../../containers/TriggerDropdown/TriggerDropdown";
import { UpdateValueDropdown } from "../../containers/UpdateValueDropdown";
import { OperatorDescriptionKinds } from "../../containers/UpdateValueDropdown/constanst";
import { SolutionColorVariables } from "@smartsuite/types";
import { CurrentOption } from "../../containers/CurrentOption";
import { usePillTooltip } from "../../../../../../../../utils/make-tooltip.hook";
import { useLoopingMenu } from "../../../../hooks/use-looping-menu";
import { DATE_FIELDS, DATE_ICONS } from "./data";
import {
  actionSourceBoundary,
  canOfferActionSources,
  resolveEditorLoopScope,
} from "../../../../utils/action-sources";
import type { LoopScope } from "../../../../../../../../stores/automation/looping.store";

const DATE_REGEX = /^.*([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])).*?/;
const TRIGGER_REGEX = /^(field-).*?/;

const canUseTime = (allowTime: boolean, time?: number): boolean => {
  return R.isNumber(time) && allowTime;
};

export enum DateTab {
  root = "root",
  "trigger-fields" = "trigger-fields",
  "action-fields" = "action-fields",
  "date-picker" = "date-picker",
  "system-fields" = "system-fields",
  "sub-menu" = "sub-menu",
  "current-item" = "current-item",
}

export const DateEditor: React.FC<{
  input: InputCandidate;
  onChange: (input: InputCandidate) => void;
  trigger: TriggerCandidate;
  actions: ActionCandidate[];
  action?: ActionCandidate;
  /** Set by ArgumentEditor when `actions` is an explicit, already-scoped list
   * (Conditional Logic branch conditions): offer it whole even though there is
   * no owning `action` to bound it (BUG-21770). */
  actionsArePreScoped?: boolean;
  /** The loop whose Current Item this editor may offer, used verbatim when
   * `actionsArePreScoped` is set. See useLoopingMenu (BUG-21770). */
  loopScope?: LoopScope;
  integrations: IntegrationDescription[];
  description?: InputDescription;
  isError?: boolean;
  onClickTrash?: () => void;
  defaultTab?: DateTab;
  filterSystemFields?: (item: {
    title: string;
    value: string;
    icon: string;
  }) => boolean;
  useDateWithTime: boolean;
  isNested?: boolean;
}> = ({
  input,
  onChange,
  trigger,
  actions,
  action,
  actionsArePreScoped,
  loopScope,
  integrations,
  description,
  isError,
  onClickTrash,
  defaultTab,
  filterSystemFields,
  useDateWithTime,
  isNested,
}) => {
  const store = useRootStore();
  const theme = useTheme();
  const { getTimeIntervalString, parseTimeOffset } = useTime();
  const { isHideInterval } = useHideInterval();
  const buttonRef = useRef();
  const commandsRef = useRef<PillPickerEditorCommands>();
  const [isOpen, setIsOpen] = useState(false);
  const [triggerMenu, setTriggerMenu] = useState<DynamicSection | null>(null);
  const [actionMenus, setActionMenus] = useState<DynamicSection[]>([]);
  const [pills, setPills] = useState<PillNodeAttrs[]>([]);
  const disableOutputClick = useRef(false);
  const [hasInterval, setHasInterval] = useState(false);
  const [hasTime, setHasTime] = useState(false);
  const [offset, setOffset] = useState<number>(0);
  const [tab, setTab] = useState<DateTab>(
    defaultTab ?? DateTab["trigger-fields"],
  );
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedActionMenu, setSelectedActionMenu] = useState<
    DynamicSection | undefined
  >();
  const [menuHeight, setMenuHeight] = useState<number>(MENU_MAX_HEIGHT);
  const [hideInterval, setHideInterval] = useState<boolean>(false);
  const [hidePast, setHidePast] = useState<boolean>(false);
  const [allowTime, setAllowTime] = useState<boolean>(false);
  const [time, setTime] = useState<number | undefined>(undefined);
  const [isTrashEnabled, setIsTrashEnabled] = useState<boolean>(false);
  const [isDefaultPills, setIsDefaultPills] = useState<boolean>(true);
  const { isInputValidByFieldMissing, getFieldMissingErrorMessage } =
    useFieldMissingValidation();
  const [subOptions, setSubOptions] = useState<DynamicSectionOptionGroup>({
    options: [],
    label: "",
  });
  const [prevTab, setPrevTab] = useState<DateTab>(DateTab.root);
  const [focus, setFocus] = useState<boolean>(false);
  const [actionType, setActionType] = useState<
    | "replace"
    | "clear"
    | "append"
    | "mathAdd"
    | "mathSubtract"
    | "mathMultiply"
    | "mathDivide"
    | undefined
  >(input.operatorConfig?.operator?.oneofKind);
  const [isCleanAction, setIsCleanAction] = useState<boolean>(false);
  const rootLooping = store.automations.candidate.looping;
  const looping = resolveEditorLoopScope(
    action,
    rootLooping,
    loopScope,
    actionsArePreScoped,
  );

  const { loopingMenu } = useLoopingMenu({
    loopScope,
    actionsArePreScoped,
    trigger,
    actions,
    integrations,
    types: [ValueDescription_T.DATE],
    ssFieldTypes: DATE_FIELDS,
    systemAllowedOptions: ["context-output-today"],
    action,
  });

  const loopingMenuList = useMemo(() => {
    if (loopingMenu === undefined) {
      return null;
    }

    return {
      ...loopingMenu,
      options: loopingMenu?.options.filter((option) =>
        DATE_ICONS.includes(option.icon),
      ),
    };
  }, [loopingMenu]);

  const { valueConfigToTargetPills } = useConverters();

  const { makeTriggerPillTooltip, makeActionPillTooltip } = usePillTooltip();

  const switchTab = (newTab: DateTab): void => {
    setPrevTab(tab);
    setTab(newTab);
  };

  useEffect(() => {
    if (window.innerHeight < menuHeight + MENU_MAX_HEIGHT_MARGIN * 2) {
      setMenuHeight(window.innerHeight - MENU_MAX_HEIGHT_MARGIN / 2);
    } else {
      setMenuHeight(MENU_MAX_HEIGHT);
    }
    window.addEventListener("resize", handleResize, false);

    return () => {
      window.removeEventListener("resize", handleResize, false);
    };
  }, []);

  useEffect(() => {
    setHideInterval(isHideInterval(pills, tab, description));
  }, [tab, pills, description]);

  useEffect(() => {
    if (description?.editor?.editor.oneofKind === "date") {
      setAllowTime(
        !!description?.editor?.editor.date.allowTime && useDateWithTime,
      );
      setHidePast(!!description?.editor?.editor.date.hidePast);
      // The default time is only for an input that holds no value yet. The
      // description is refreshed again when an account is connected on the
      // action, and the stored value's own time must survive that.
      if (pills.length === 0) {
        const defaultTime =
          description?.editor?.editor.date.allowTime === true
            ? description?.editor.editor.date.defaultTime
            : undefined;
        setTime(defaultTime);
        setHasTime(!!defaultTime);
      }
    }
    setIsTrashEnabled(
      description?.display?.display.oneofKind === "group" &&
        description.display?.display.group.groupId === "ss-fields-group",
    );
  }, [description]);

  useEffect(() => {
    if (actionType === undefined) return;
    if (actionType === OperatorDescriptionKinds.CLEAR) {
      setIsOpen(false);
      setIsCleanAction(true);
    } else {
      setIsCleanAction(false);
    }
    onChange({
      ...input,
      value:
        actionType === OperatorDescriptionKinds.CLEAR
          ? {
              values: [],
            }
          : input.value,
      operatorConfig: {
        // @ts-expect-error
        operator: {
          oneofKind: actionType,
          [actionType]: {},
        },
      },
    });
  }, [actionType, isOpen]);

  const handleResize = (): void => {
    if (window.innerHeight < menuHeight + MENU_MAX_HEIGHT_MARGIN * 2) {
      setMenuHeight(window.innerHeight - MENU_MAX_HEIGHT_MARGIN / 2);
    } else {
      setMenuHeight(MENU_MAX_HEIGHT);
    }
  };

  const setCurrentDate = (pill: PillNodeAttrs): void => {
    const val = JSON.parse(pill.value);
    if (val.target === "date" || val.originalTarget === "date") {
      setSelectedDate(
        convertDateToBrowserTimezone(
          new Date(val.valueUTC),
          store.automations.candidate.getTimezone(),
        ),
      );
    }
  };

  useEffect(() => {
    const initValue: Array<{
      offset: number;
      time: number | undefined;
    }> = [];
    const result = pills.reduce((prev, pill) => {
      const value = JSON.parse(pill.value);
      if ("offset" in value) {
        prev.push({
          offset: parseDaysOffset(value.offset),
          time: parseTimeOffset(value.offset),
        });
      }
      return prev;
    }, initValue);
    if (result.length === 0) {
      setHasInterval(false);
      setHasTime(false);
    } else {
      setHasInterval(true);
      setHasTime(true);
      setOffset(result[0].offset);
      setTime(result[0].time);
    }
    if (pills.length > 0) {
      setCurrentDate(pills[0]);
    }
  }, [pills]);

  const changeActionType = (
    type:
      | "replace"
      | "clear"
      | "append"
      | "mathAdd"
      | "mathSubtract"
      | "mathMultiply"
      | "mathDivide"
      | undefined,
  ): void => {
    setActionType(type);
  };

  const addPill = (attrs: PillNodeAttrs): void => {
    let newPills: PillNodeAttrs[];
    const value = JSON.parse(attrs.value);
    if (value.target === "trigger") {
      attrs.tooltip = makeTriggerPillTooltip(trigger);
    } else if (value.target === "action") {
      const action = actions.find((action) => action.hash === value.hash);
      if (action) {
        attrs.tooltip = makeActionPillTooltip(action);
      }
    }
    const newPill = updatePillWhenAdd(attrs, true);
    if (input.valueDescription?.multiple === true) {
      newPills = [...pills, newPill];
    } else {
      newPills = [newPill];
      setTimeout(() => setIsOpen(false));
    }
    setPills(newPills);

    onChange({
      ...input,
      value: calculateConfigValue(newPills, actions, trigger),
    });
  };

  const removePill = (_: number, pill: FieldTypePillProps): void => {
    const newPills = pills.filter((item) => item !== pill);
    setPills(newPills);
    setIsDefaultPills(false);
    onChange({
      ...input,
      value: calculateConfigValue(newPills, actions, trigger),
    });
  };

  const updatePillWhenAdd = (
    pill: PillNodeAttrs,
    skipOffset = false,
    time?: number | undefined,
  ): PillNodeAttrs => {
    if (allowTime || offset !== 0 || time !== undefined) {
      const value = JSON.parse(pill.value);
      return {
        ...pill,
        value: JSON.stringify({
          ...value,
          offset: daysToOffset(
            skipOffset ? 0 : offset,
            getTimeIntervalString(time),
          ),
          originalTarget: value.target,
          target: "date_offset_function",
          startOfDay: canUseTime(allowTime, time),
          time: canUseTime(allowTime, time) ? time : undefined,
        }),
        // tooltip: {
        // title: "123123"
        // }
      };
    }
    return pill;
  };

  const updatePillsWithDateOffset = async (offset: string): Promise<void> => {
    const newPills = await Promise.all(
      pills.map((pill) => {
        const value = JSON.parse(pill.value);
        return {
          ...pill,
          value: JSON.stringify({
            ...value,
            offset,
            originalTarget: value.target,
            target: "date_offset_function",
            startOfDay: canUseTime(allowTime, time),
          }),
        };
      }),
    );
    setPills(newPills);
    onChange({
      ...input,
      value: calculateConfigValue(newPills, actions, trigger),
    });
  };

  const updatePillsWithNewOffset = async (
    offset: number,
    time?: number,
  ): Promise<void> => {
    if (!hasInterval || !hasTime) {
      setHasInterval(true);
      setHasTime(true);
      await updatePillsWithDateOffset(
        daysToOffset(offset, getTimeIntervalString(time)),
      );
    } else {
      const newPills = await Promise.all(
        pills.map((pill) => {
          const value = JSON.parse(pill.value);
          return {
            ...pill,
            value: JSON.stringify({
              ...value,
              offset: daysToOffset(offset, getTimeIntervalString(time)),
              startOfDay: canUseTime(allowTime, time),
            }),
          };
        }),
      );
      setPills(newPills);
      onChange({
        ...input,
        value: calculateConfigValue(newPills, actions, trigger),
      });
    }
  };

  useEffect(() => {
    setTriggerMenu(
      convertTriggerToDynamicSection(
        trigger,
        integrations,
        [ValueDescription_T.DATE],
        [ValueDescription_T.DATE],
        [
          "datefield",
          "daterangefield",
          "duedatefield",
          "firstcreatedfield",
          "lastupdatedfield",
        ],
        ["context-output-today"],
      ),
    );
    if (canOfferActionSources(action, actionsArePreScoped)) {
      setActionMenus(
        convertActionsToDynamicSections(
          actions,
          actionSourceBoundary(action, actionsArePreScoped),
          integrations,
          [ValueDescription_T.DATE],
          [
            "datefield",
            "daterangefield",
            "duedatefield",
            "firstcreatedfield",
            "lastupdatedfield",
          ],
          ["context-output-today"],
          looping,
        ),
      );
    }
  }, [trigger, action, actions, integrations, actionsArePreScoped, loopScope]);

  // set default pills
  useEffect(() => {
    if (R.isNil(input.value)) return;

    const defaultPills = [];
    if (
      isDefaultPills &&
      description?.editor?.editor.oneofKind === "date" &&
      description?.editor.editor.date.defaultDate !== undefined
    ) {
      defaultPills.push(
        updatePillWhenAdd(
          description.editor.editor.date.defaultDate,
          false,
          description?.editor.editor.date.defaultTime,
        ),
      );
    }

    const incomePills = valueConfigToTargetPills(
      input.value,
      trigger,
      actions,
      store.automations.candidate.getTimezone(),
      (label) => label,
      undefined,
      undefined,
      undefined,
      loopingMenuList?.options ?? [],
    );

    if (incomePills.length > 0) {
      onChange({
        ...input,
        value: calculateConfigValue(incomePills, actions, trigger),
      });
      setPills(incomePills);
    } else if (defaultPills.length > 0) {
      onChange({
        ...input,
        value: calculateConfigValue(defaultPills, actions, trigger),
      });
      setPills(defaultPills);
    }
  }, []);

  useEffect(() => {
    disableOutputClick.current = tab === "date-picker";
  }, [tab]);

  useEffect(() => {
    const pillValue = JSON.parse(pills[0]?.value ?? "{}")?.value ?? "";

    if (triggerMenu?.options.length === 0) {
      switchTab(DateTab.root);
    } else if (DATE_REGEX.test(pillValue)) {
      switchTab(DateTab["date-picker"]);
    } else if (TRIGGER_REGEX.test(pillValue)) {
      switchTab(DateTab["trigger-fields"]);
    } else {
      switchTab(defaultTab ?? DateTab["system-fields"]);
    }
  }, [triggerMenu]);

  useEffect(() => {
    if (
      store.inputToFocus?.inputId === input.inputId &&
      description?.editor?.editor.oneofKind === "date" // do not open dropdown for checklist
    ) {
      setIsOpen(true);
      store.inputToFocus = undefined;
    }
  }, []);

  const triggerOutputs = triggerMenu?.options ?? [];
  // const triggerOutputs =
  // triggerMenu?.options.filter((option) => !option.contextSpecific) ?? [];

  const goSubMenu = (options: DynamicSectionOption[], label: string): void => {
    setSubOptions({ options, label });
    switchTab(DateTab["sub-menu"]);
  };

  const getPrevTab = (prevTab: DateTab): Tab => {
    if (prevTab === "trigger-fields") {
      return Tab.TRIGGER_FIELDS;
    }
    if (prevTab === "action-fields") {
      return Tab.ACTION_FIELDS;
    }
    if (prevTab === "current-item") {
      return Tab.CURRENT_ITEM;
    }
    return Tab.ROOT;
  };

  const disabledOptionId = pills.map((pill) => pill.title);

  const buildMenu = (): React.ReactNode => {
    return (
      <DropdownPanel
        targetElementRef={buttonRef}
        isActive={isOpen}
        at={POSITION_AT}
        my={POSITION_MY}
        minWidth={tab === "date-picker" ? 415 : 300}
        arrowBackground="custom"
        arrowCustomBackground={SolutionColorVariables.sc1}
        panelLineBackground={SolutionColorVariables.sc1}
        portalStyles={getLocalCssColorVariables(theme)}
        onClickOutside={(event) => {
          !checkIfIsPickerInputField(event.target, input.inputId) &&
            setIsOpen(false);
        }}
      >
        {tab === "root" && (
          <MenuOptionWrapper>
            <MenuOption
              submenuArrow="right"
              onClick={() => switchTab(DateTab["system-fields"])}
            >
              {t("automation-form.date-actions")}
            </MenuOption>
            <MenuOption
              key="Select specific date"
              submenuArrow="right"
              onClick={() => switchTab(DateTab["date-picker"])}
            >
              {t("automation-form.specific-date")}
            </MenuOption>
            {triggerOutputs.length > 0 && (
              <MenuOption
                submenuArrow="right"
                onClick={() => switchTab(DateTab["trigger-fields"])}
              >
                {t("automation-form.dates-from-trigger")}
              </MenuOption>
            )}

            {actionMenus.map(
              (actionMenu) =>
                actionMenu?.options.length > 0 && (
                  <MenuOption
                    key={actionMenu.hash}
                    submenuArrow="right"
                    onClick={() => {
                      setSelectedActionMenu(actionMenu);
                      switchTab(DateTab["action-fields"]);
                    }}
                  >
                    {actionMenu.hideNamePrefix === true
                      ? actionMenu.name
                      : t("field-popups.action-popup", {
                          name: actionMenu.name,
                        })}
                  </MenuOption>
                ),
            )}

            {Boolean(loopingMenuList?.options.length) && (
              <CurrentOption
                loopingMenu={loopingMenuList}
                loopScope={looping}
                actionsArePreScoped={actionsArePreScoped}
                addPill={() => {
                  switchTab(DateTab["current-item"]);
                }}
              />
            )}
          </MenuOptionWrapper>
        )}
        {tab === "trigger-fields" && (
          <TriggerOptions
            searchPlaceholder={t("automation-form.search-fields")}
            title={t("automation-form.dates-from-trigger")}
            timezone={store.automations.candidate.getTimezone()}
            onNavigateBack={() => switchTab(DateTab.root)}
            addPill={addPill}
            goSubMenu={goSubMenu}
            options={triggerOutputs}
            disabledOptionId={pills.map((pill) => pill.title)}
          />
        )}
        {tab === "system-fields" && (
          <TriggerOptions
            searchPlaceholder={t("automation-form.search-fields")}
            title={t("automation-form.date-actions")}
            onNavigateBack={() => switchTab(DateTab.root)}
            timezone={store.automations.candidate.getTimezone()}
            addPill={addPill}
            options={[]}
            addDateOptions={true}
            dateOptionsWithTime={useDateWithTime}
            filterSystemFields={filterSystemFields}
            hidePast={hidePast}
          />
        )}
        {tab === "sub-menu" && (
          <SubMenuOptions
            title={subOptions.label}
            hasSingleMenuItem={
              triggerOutputs.length === 0 &&
              actionMenus.length === 0 &&
              loopingMenu?.options.length === 0
            }
            onNavigateBack={() => switchTab(prevTab)}
            addPill={addPill}
            options={subOptions.options}
            selectedActionHash={selectedActionMenu?.hash}
            loopingMenuHash={loopingMenu?.hash}
            prevTab={getPrevTab(prevTab)}
          />
        )}
        {tab === "current-item" && Boolean(loopingMenuList?.options.length) && (
          <TriggerOptions
            tab={Tab.LOOPING_FIELDS}
            searchPlaceholder={"Search Fields"}
            title={loopingMenuList?.name}
            timezone={store.automations.candidate.getTimezone()}
            onNavigateBack={() => switchTab(DateTab.root)}
            addPill={addPill}
            options={loopingMenuList?.options ?? []}
            hasSingleMenuItem={false}
            goSubMenu={goSubMenu}
            loopingMenuHash={loopingMenuList?.hash}
            loopingMenu={loopingMenuList}
            disabledOptionId={disabledOptionId}
          />
        )}
        {tab === "action-fields" && (
          <ActionOptions
            onNavigateBack={() => switchTab(DateTab.root)}
            addPill={addPill}
            selectedActionMenu={selectedActionMenu}
            selectedActionHash={selectedActionMenu?.hash}
            goSubMenu={goSubMenu}
          />
        )}
        {tab === "date-picker" && (
          <DatePickerWrapper
            onMouseEnter={() => {
              disableOutputClick.current = true;
            }}
            onMouseLeave={() => {
              disableOutputClick.current = false;
            }}
          >
            <MenuGroupContent
              title={t("automation-form.specific-date")}
              subtitle={getTimezoneShortLabel(
                store.automations.candidate.getTimezone(),
              )}
              onNavigateBack={() => switchTab(DateTab.root)}
              search={false}
              maxHeight={menuHeight}
            >
              <DatePicker
                hideHeader={true}
                showTimePicker={false}
                value={selectedDate}
                onChange={(date: Date) => {
                  if (
                    date.getFullYear() !== selectedDate.getFullYear() &&
                    date.getDate() === selectedDate.getDate() &&
                    date.getMonth() === selectedDate.getMonth()
                  ) {
                    // it means - only year was changed - so we don't need to close the popup
                    return;
                  }
                  setSelectedDate(date);
                  const newDate = createDateInUserTimezone(
                    date,
                    store.automations.candidate.getTimezone(),
                  );
                  addPill({
                    icon: "",
                    title: date.toLocaleDateString(),
                    value: JSON.stringify({
                      value: date,
                      target: "date",
                      valueUTC: newDate,
                    }),
                  });
                }}
                onSelectionDone={() => {
                  const newDate = createDateInUserTimezone(
                    selectedDate,
                    store.automations.candidate.getTimezone(),
                  );
                  addPill({
                    icon: "",
                    title: selectedDate.toLocaleDateString(),
                    value: JSON.stringify({
                      value: newDate,
                      target: "date",
                    }),
                  });
                }}
                onSelectionCanceled={() => {
                  setIsOpen(false);
                  switchTab(DateTab.root);
                }}
                zIndex={1000}
                locale={getLocaleByLangCode(i18n.language)}
                hideFooter={true}
              />
            </MenuGroupContent>
          </DatePickerWrapper>
        )}
      </DropdownPanel>
    );
  };

  return (
    <PickerStyleWrapper>
      <div>
        {description !== undefined && (
          <CaptionBlock
            description={description}
            onClickTrash={onClickTrash}
            interval={false}
          />
        )}
        <InputWrapper>
          <PickerInputField
            showAddButton={!isCleanAction}
            className={
              isError
                ? `is-error picker-input-field-${input.inputId} ${
                    isCleanAction ? "is-clear" : ""
                  }`
                : `picker-input-field-${input.inputId} ${
                    isCleanAction ? "is-clear" : ""
                  }`
            }
            state={getEditorState(
              isOpen || focus,
              !isInputValidByFieldMissing(input, action),
            )}
            buttonRef={buttonRef}
            // @ts-expect-error
            commandsRef={commandsRef}
            isOpen={isOpen}
            pills={pills}
            pillStyle={{
              textSize: "m",
            }}
            onButtonClick={() => setIsOpen((open) => !open)}
            onPillRemove={removePill}
            autofocus={false}
            placeholder={
              isCleanAction
                ? t("automation-form.clean-operator-editor-placeholder")
                : isOpen
                  ? t("automation-form.date-field-placeholder-new")
                  : ""
            }
            onClick={() => {
              setIsOpen(true);
            }}
          >
            {action?.actionReference?.actionId === ActionLabel.updateRecord &&
              description?.operators?.operators !== undefined &&
              description?.operators?.operators.length > 0 &&
              !isNested && (
                <UpdateValueDropdown
                  operatorConfig={input.operatorConfig}
                  operatorDescriptions={description?.operators.operators}
                  onOpenedChange={setFocus}
                  onChange={changeActionType}
                />
              )}

            {isOpen && buildMenu()}
          </PickerInputField>
          {!hideInterval && (
            <NumberInput
              onChange={(value) => {
                setOffset(value);
                if (pills.length > 0) {
                  void updatePillsWithNewOffset(value, time);
                }
              }}
              value={offset}
              disabled={false}
              interval={false}
              isTrashEnabled={!allowTime && isTrashEnabled}
            />
          )}
          {allowTime && (
            <TimeEditor
              isSet={hasTime}
              time={time}
              disabled={false}
              onChange={(val) => {
                setHasTime(true);
                setTime(val);
                if (pills.length > 0) {
                  void updatePillsWithNewOffset(offset ?? 0, val);
                }
              }}
            />
          )}
        </InputWrapper>
      </div>
      <FieldMissingError message={getFieldMissingErrorMessage(input, action)} />
    </PickerStyleWrapper>
  );
};

const getEditorState = (
  isOpen: boolean,
  isError?: boolean,
): "active" | "default" | "error" => {
  if (isError) {
    return "error";
  } else if (isOpen) {
    return "active";
  } else {
    return "default";
  }
};
